import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";

import routes from "../../routes";
import useAuth from "../../hooks/useAuth";
import { LayoutContext } from "../../context/LayoutContext";
import BaseWrapper from "../../components/ui/BaseWrapper";
import {
  brandFontFamily,
  brandInputTextColor,
  brandPrimaryColor,
} from "../../utils/variables";

import PermissionsHeader from "./components/PermissionsHeader";
import { fetchAllPermissionsByDesignation } from "../../services/api/designation";
import { useQuery } from "@tanstack/react-query";

function CustomTabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`we-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const CustomTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: "none",
    minWidth: 0,
    height: "38px",
    width: "230px",
    fontWeight: 300,
    fontSize: "16px",
    boxShadow: "0px -1px 8px 0px rgba(0,0,0,0.09)",
    borderRadius: "6px",
    margin: "20px 0",
    background: "white",
    marginRight: theme.spacing(1),
    color: brandInputTextColor,
    fontFamily: brandFontFamily,
    "&.Mui-selected": {
      color: brandPrimaryColor,
      fontWeight: 400,
      borderBottom: `1px solid ${brandPrimaryColor}`,
    },
  })
);

export default function Permissions() {
  const { isBannerVisible } = useContext(LayoutContext);
  const { authRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const { role: desgination, id: designationId } = useParams();

  const {
    isPending: isFetchAllPermissionsByDesignationApiLoading,
    data: designationPermissions,
    refetch: refetchAllPermissionsByDesignation,
  } = useQuery({
    queryKey: ["designationPermissions", designationId],
    queryFn: () => fetchAllPermissionsByDesignation(designationId),
    enabled: !!designationId,
  });

  const permissionRoutes = routes.find(
    (route) => route.path === `/company/:role/permissions/:id`
  );

  const allTabs = permissionRoutes?.childs || [];
  const visibleTabs = allTabs.filter((tab) => tab?.roles?.includes(authRole));

  useEffect(() => {
    const currentTabIndex = visibleTabs?.findIndex((tab) => {
      return location.pathname.endsWith(
        `/company/${desgination}/permissions/${designationId}/${tab.path}`
      );
    });

    if (currentTabIndex !== -1) {
      setValue(currentTabIndex);
    } else {
      // If no tab matches the current URL, navigate to the first tab by default
      navigate(
        `/company/${desgination}/permissions/${designationId}/${visibleTabs[0]?.path}`,
        {
          replace: true,
        }
      );
      setValue(0);
    }
  }, [location.pathname, visibleTabs]);

  const handleChange = (event, newValue) => {
    const selectedTab = visibleTabs[newValue];

    navigate(
      `/company/${desgination}/permissions/${designationId}/${selectedTab.path}`
    );
    setValue(newValue);
  };

  return (
    <div className="bg-light">
      <div
        className={`bg-[#F6F6F6] sm:fixed 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full h-screen transition-all ${
          isBannerVisible ? "top-[106px]" : "top-[56px]"
        }`}
      >
        <PermissionsHeader />

        <BaseWrapper>
          <Tabs
            value={value}
            onChange={handleChange}
            TabIndicatorProps={{ style: { display: "none" } }}
            aria-label="work experience tabs"
          >
            {visibleTabs.map((t, i) => (
              <CustomTab
                key={t.path}
                label={t.text}
                id={`we-tab-${i}`}
                aria-controls={`we-tabpanel-${i}`}
                sx={{
                  minHeight: "38px",
                  marginRight: "40px",
                }}
              />
            ))}
          </Tabs>

          {visibleTabs.map((t, i) => (
            <CustomTabPanel key={t.path} value={value} index={i}>
              {React.cloneElement(t.component, {
                designationPermissions: designationPermissions?.data,
                designationId,
                refetchAllPermissionsByDesignation,
              })}
            </CustomTabPanel>
          ))}
        </BaseWrapper>
      </div>
    </div>
  );
}
